import { useRef, useCallback } from 'react'
import analytics from 'global/AnalyticsData'

export const useStepAnalytics = brk => {
  const stepStartDateRef = useRef(null)

  // Track how many times each step has been viewed in this page session.
  // Resets on page reload, used to prevent duplicate break_completion events.
  const stepViewCountsRef = useRef(
    brk.steps.reduce((map, step) => {
      map[step.id] = 0
      return map
    }, {}),
  )

  const lastStepViewRef = useRef(null)

  const trackStepView = useCallback(
    stepId => {
      stepViewCountsRef.current[stepId] =
        (stepViewCountsRef.current[stepId] || 0) + 1

      if (lastStepViewRef.current !== stepId) {
        lastStepViewRef.current = stepId
        const step = brk.steps.find(s => s.id === stepId)
        analytics.stepEvent('step_view', step)
      }
    },
    [brk],
  )

  const trackStepStart = useCallback(
    (step, attempt = 1, isBreakEngagement = false) => {
      console.log('⚠️ LOG: Start step:', step.type)
      stepStartDateRef.current = new Date()

      if (typeof window !== 'undefined') {
        if (isBreakEngagement) analytics.breakEvent('break_engagement', brk)
        if (step.id === brk.steps[0].id) analytics.breakEvent('break_start', brk)
        analytics.stepEvent('step_start', step)
      }
    },
    [brk],
  )

  const trackStepComplete = useCallback(
    completedStepId => {
      const step = brk.steps.find(s => s.id === completedStepId)
      const elapsed = Math.floor(
        (new Date() - (stepStartDateRef.current ?? new Date())) / 1000,
      )

      analytics.stepEvent('step_completion', step, { elapsed_time: elapsed })

      // Only fire once per page session (stepViewCounts resets on refresh)
      if (
        brk.steps[brk.steps.length - 1].id === completedStepId &&
        (stepViewCountsRef.current[completedStepId] || 0) <= 1
      ) {
        analytics.breakEvent('break_completion', brk)
      }
    },
    [brk],
  )

  const trackStepSkip = useCallback(
    stepId => {
      const step = brk.steps.find(s => s.id === stepId)
      const elapsed = Math.floor(
        (new Date() - (stepStartDateRef.current ?? new Date())) / 1000,
      )

      analytics.stepEvent('step_skip', step, { elapsed_time: elapsed })
      analytics.stepEvent('step_completion', step, { elapsed_time: elapsed })
    },
    [brk],
  )

  return {
    trackStepView,
    trackStepStart,
    trackStepComplete,
    trackStepSkip,
  }
}
